// src/components/StyledComponents/LargeImage.tsx
import React from 'react';
import styled from 'styled-components';
import preventDefaultEvents from '../hoc/preventDefaultEvents';
import { MEDIA_BREAKPOINTS} from '@/constants/layout';

type ImageType =
  | 'largeImage'
  | 'horizontalImage'
  | 'portraitImage'
  | 'squareImage'
  | 'bannerImage';

interface CustomImageProps {
  $imageType: ImageType;
  $gradientAngle: string;
}

type StyledImageProps = React.ImgHTMLAttributes<HTMLImageElement> & CustomImageProps;

const ImageWrapper = styled.div`
  border-radius: 90px;
  overflow: hidden;
  display: flex;
  justify-content: center;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding: 10px;
  }

  width: 100%;
  max-width: min(92vw, 700px);
`;

const StyledImage = styled.img<StyledImageProps>`
  display: block;
  margin: 0 auto;
  border-radius: 40px;
  border: 3px solid transparent;
  border-image: linear-gradient(
    ${({ $gradientAngle }) => $gradientAngle},
    ${({ theme }) => theme.gradientColors[0]},
    ${({ theme }) => theme.gradientColors[1]}
  );
  border-image-slice: 1;
  width: 100%;
  height: auto;
  object-fit: contain;
  max-height: 70vh;
  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    ${({ $imageType }) =>
      $imageType === 'horizontalImage' &&
      `
        max-height: clamp(160px, 58vh, 520px);
        width: 92%;
      `}
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    max-height: 65vh; 
  }
  @media (min-width: ${MEDIA_BREAKPOINTS.largeScreen}) {
    max-height: 60vh; 
  }
`;

interface ImageProps {
  src: string;
  alt?: string;
  imageType: ImageType;
  gradientAngle?: string;
  srcSet?: string;
  sizes?: string;
  intrinsicSize?: {
    width: number;
    height: number;
  };
}

const LargeImage: React.FC<ImageProps> = ({
  src,
  alt,
  imageType,
  gradientAngle = '240deg',
  srcSet,
  sizes,
  intrinsicSize,
}) => {
  return (
    <ImageWrapper>
      <StyledImage
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        width={intrinsicSize?.width}
        height={intrinsicSize?.height}
        loading="lazy"
        decoding="async"
        $imageType={imageType}
        $gradientAngle={gradientAngle}
      />
    </ImageWrapper>
  );
};

export default preventDefaultEvents(LargeImage);
